import { groupBy, isEqual, keys } from 'lodash'
import { useMemo, useState } from 'react'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { ChannelMappingForm } from 'app/components/mapping/ChannelMappingForm'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { ChannelMappingType, defaultChannelMappings, defaultChannels } from 'lib/channelDefaults'
import { logError } from 'lib/logging'
import { useChannelMappingStore, useEntityList } from 'app/stores/StoreContext'
import { observer } from 'mobx-react-lite'

export type ChannelMappingsLoaderData = {
  channelMappings: { hits: ChannelMappingType[] }
  canEditMapping: boolean
}

export type GroupedChannelMappings = Record<string, ChannelMappingType[]>

export type UseChannelMappingsReturn = {
  canEditMapping: boolean
  channelUpdate: string | undefined
  groupedChannelMappings: GroupedChannelMappings
  defaultChannels: string[]
  hasData: boolean
  onAdd: () => void
  onEdit: (channel: ChannelMappingType) => void
  onDelete: (id: string, channel: string) => Promise<void>
  isDefaultMapping: (mapping: ChannelMappingType) => boolean
}

export function useChannelMappings(): UseChannelMappingsReturn {
  const [confirm] = useConfirmation()
  const checkPermission = useCheckPermission()
  const canEditMapping = checkPermission([`org:settings:edit-mapping`, `settings:mapping:edit`])
  const store = useChannelMappingStore()
  const [channelUpdate, setChannelUpdate] = useState<string | undefined>()

  const { list } = useEntityList(store)
  const channelMappings = list as unknown as ChannelMappingType[]

  const groupedChannelMappings = useMemo(
    () =>
      groupBy(
        [
          ...channelMappings,
          ...defaultChannelMappings.map(([source, medium, channel]) => ({ source, medium, channel })),
        ],
        `channel`,
      ),
    [channelMappings],
  )

  const isDefaultMapping = (mapping: ChannelMappingType) =>
    defaultChannelMappings.some((dm) => isEqual(dm, [mapping.source, mapping.medium, mapping.channel]))

  const openModal = ({
    title,
    channel,
    isDefaultChannel,
  }: {
    title: string
    channel?: ChannelMappingType
    isDefaultChannel: boolean
  }) =>
    showModal(
      <ChannelMappingForm
        channel={channel}
        isDefaultChannel={isDefaultChannel}
        onSubmit={({ channelMapping }) => setChannelUpdate(channelMapping.channel)}
      />,
      { title, isFullScreen: false, removePadding: true, useRadix: true },
    )

  const onAdd = () => openModal({ title: `Create New Channel Mapping`, isDefaultChannel: false })

  const onEdit = (channel: ChannelMappingType) =>
    openModal({ title: `Edit Channel Mapping`, channel, isDefaultChannel: isDefaultMapping(channel) })

  const onDelete = async (id: string, channel: string) => {
    await confirm({
      title: `Delete Channel Mapping`,
      description: `Are you sure you want to delete this channel mapping?`,
      variant: `danger`,
    }).then(async () => {
      setChannelUpdate(channel)
      const result = await store.remove(id)
      if (result.error) {
        logError(`Error deleting channel mapping`, result.error)
        notifyError(`Error deleting channel mapping`)
      } else {
        notifySuccess(`Deleted Successfully`)
        store.fetchList()
      }
    })
  }

  return {
    canEditMapping,
    channelUpdate,
    groupedChannelMappings,
    defaultChannels,
    hasData: keys(groupedChannelMappings).length > 0,
    onAdd,
    onEdit,
    onDelete,
    isDefaultMapping,
  }
}
